import { graphql } from '../gql';

// ===========================
// CHECKLIST TEMPLATE QUERIES
// ===========================

export const getChecklistTemplatesByTeam = graphql(/* GraphQL */ `
  query GetChecklistTemplatesByTeam($teamId: ID!) {
    checklistTemplatesByTeam(teamId: $teamId) {
      id
      name
      description
      sharedToAffiliatedWorkspaces
      createdAt
      updatedAt
      items {
        id
        content
        displayOrder
        defaultDueOffsetDays
        children {
          id
          content
          displayOrder
          defaultDueOffsetDays
        }
      }
    }
  }
`);

// ===========================
// DEAL CHECKLIST QUERIES
// ===========================

export const getChecklistsByDeal = graphql(/* GraphQL */ `
  query GetChecklistsByDeal($dealId: ID!) {
    checklistsByDeal(dealId: $dealId) {
      id
      uuid
      name
      completedCount
      totalCount
      createdAt
      creator {
        id
        fullName
        email
      }
      dealId
      items {
        id
        content
        completed
        completedAt
        displayOrder
        dueDate
        reminderDaysBefore
        autoCompleteOnSignature
        completedBy {
          id
          fullName
        }
        assignedTo {
          id
          fullName
        }
        document {
          id
          title
        }
        children {
          id
          content
          completed
          completedAt
          displayOrder
          dueDate
          reminderDaysBefore
          autoCompleteOnSignature
          completedBy {
            id
            fullName
          }
          assignedTo {
            id
            fullName
          }
          document {
            id
            title
          }
        }
      }
    }
  }
`);

export const getDealMembersForAssignment = graphql(/* GraphQL */ `
  query GetDealMembersForAssignment($dealId: ID!) {
    dealMembersForAssignment(dealId: $dealId) {
      id
      fullName
      email
    }
  }
`);

// ===========================
// TEMPLATE MUTATIONS
// ===========================

export const createChecklistTemplate = graphql(/* GraphQL */ `
  mutation CreateChecklistTemplate($teamId: ID!, $name: String!, $description: String, $items: JSON) {
    createChecklistTemplate(teamId: $teamId, name: $name, description: $description, items: $items) {
      checklistTemplate {
        id
        name
        description
        sharedToAffiliatedWorkspaces
        createdAt
      }
      errors
    }
  }
`);

export const updateChecklistTemplate = graphql(/* GraphQL */ `
  mutation UpdateChecklistTemplate($id: ID!, $name: String, $description: String, $items: JSON) {
    updateChecklistTemplate(id: $id, name: $name, description: $description, items: $items) {
      checklistTemplate {
        id
        name
        description
        updatedAt
      }
      errors
    }
  }
`);

export const deleteChecklistTemplate = graphql(/* GraphQL */ `
  mutation DeleteChecklistTemplate($id: ID!) {
    deleteChecklistTemplate(id: $id) {
      success
      errors
    }
  }
`);

export const shareChecklistTemplateToWorkspaces = graphql(/* GraphQL */ `
  mutation ShareChecklistTemplateToWorkspaces($templateId: ID!) {
    shareChecklistTemplateToWorkspaces(templateId: $templateId) {
      checklistTemplate {
        id
        sharedToAffiliatedWorkspaces
      }
      affiliatedWorkspaceCount
      isShared
      errors
    }
  }
`);

export const cloneChecklistTemplate = graphql(/* GraphQL */ `
  mutation CloneChecklistTemplate($templateId: ID!, $targetTeamId: ID!) {
    cloneChecklistTemplate(templateId: $templateId, targetTeamId: $targetTeamId) {
      checklistTemplate {
        id
        name
        description
      }
      errors
    }
  }
`);

// ===========================
// CHECKLIST MUTATIONS
// ===========================

export const createChecklistFromTemplates = graphql(/* GraphQL */ `
  mutation CreateChecklistFromTemplates($dealId: ID!, $name: String!, $templateIds: [ID!]!, $anchorDate: ISO8601Date) {
    createChecklistFromTemplates(dealId: $dealId, name: $name, templateIds: $templateIds, anchorDate: $anchorDate) {
      dealChecklist {
        id
        uuid
        name
        completedCount
        totalCount
        createdAt
      }
      errors
    }
  }
`);

export const addItemsFromTemplate = graphql(/* GraphQL */ `
  mutation AddItemsFromTemplate($checklistId: ID!, $templateId: ID!, $anchorDate: ISO8601Date) {
    addItemsFromTemplate(checklistId: $checklistId, templateId: $templateId, anchorDate: $anchorDate) {
      dealChecklist {
        id
        uuid
        name
        completedCount
        totalCount
        items {
          id
          content
          completed
          completedAt
          displayOrder
          dueDate
          children {
            id
            content
            completed
            displayOrder
          }
        }
      }
      addedItemsCount
      errors
    }
  }
`);

export const deleteChecklist = graphql(/* GraphQL */ `
  mutation DeleteChecklist($id: ID!) {
    deleteChecklist(id: $id) {
      success
      errors
    }
  }
`);

export const addChecklistItem = graphql(/* GraphQL */ `
  mutation AddChecklistItem(
    $checklistId: ID!
    $content: String!
    $parentItemId: ID
    $dueDate: ISO8601Date
    $reminderDaysBefore: Int
    $documentId: ID
    $assignedToId: ID
    $autoCompleteOnSignature: Boolean
  ) {
    addChecklistItem(
      checklistId: $checklistId
      content: $content
      parentItemId: $parentItemId
      dueDate: $dueDate
      reminderDaysBefore: $reminderDaysBefore
      documentId: $documentId
      assignedToId: $assignedToId
      autoCompleteOnSignature: $autoCompleteOnSignature
    ) {
      dealChecklistItem {
        id
        content
        completed
        displayOrder
        dueDate
        reminderDaysBefore
        autoCompleteOnSignature
        assignedTo {
          id
          fullName
        }
        document {
          id
          title
        }
      }
      errors
    }
  }
`);

export const updateChecklistItem = graphql(/* GraphQL */ `
  mutation UpdateChecklistItem(
    $id: ID!
    $content: String
    $completed: Boolean
    $dueDate: ISO8601Date
    $reminderDaysBefore: Int
    $documentId: ID
    $assignedToId: ID
    $autoCompleteOnSignature: Boolean
    $displayOrder: Int
    $parentItemId: ID
  ) {
    updateChecklistItem(
      id: $id
      content: $content
      completed: $completed
      dueDate: $dueDate
      reminderDaysBefore: $reminderDaysBefore
      documentId: $documentId
      assignedToId: $assignedToId
      autoCompleteOnSignature: $autoCompleteOnSignature
      displayOrder: $displayOrder
      parentItemId: $parentItemId
    ) {
      dealChecklistItem {
        id
        content
        completed
        completedAt
        completedBy {
          id
          fullName
        }
        displayOrder
        dueDate
        reminderDaysBefore
        autoCompleteOnSignature
        assignedTo {
          id
          fullName
        }
        document {
          id
          title
        }
      }
      errors
    }
  }
`);

export const deleteChecklistItem = graphql(/* GraphQL */ `
  mutation DeleteChecklistItem($id: ID!) {
    deleteChecklistItem(id: $id) {
      success
      errors
    }
  }
`);
